import { useEffect, useState, useCallback } from "react";
import { LockKeyhole, Plug } from "lucide-react";
import { theme, card, emptyFrame, inputStyle, btnPrimary } from "../theme";
import { IndexTabs, RulerProgress } from "../components/Drawing";
import { LoadingBlock } from "../components/Spinner";
import { api, ApiError } from "../api/client";
import { RaiseIncidentCell, useIncidentSourceLinks } from "./resilience/IncidentSources";
import { ConnectorTrendPanel } from "../components/ConnectorTrendPanel";

interface Settings {
  identityEnabled: boolean;
  scoreEnabled:    boolean;
  alertsEnabled:   boolean;
  caEnabled:       boolean;
  dlpEnabled:      boolean;
  auditEnabled:    boolean;
  vulnEnabled:     boolean;
  guestEnabled?:   boolean;
}

/** Supplier guest accounts in this tenant, matched to the supplier register (backend services/supplierGuestAccess.ts). */
interface SupplierGuests {
  enabled: boolean;
  /** False while the supplier register (TPRM add-on) is off: nothing is read then. */
  available: boolean;
  lastSync: string | null;
  error: string | null;
  accounts: number;
  matched: number;
  requiredPermissions: string[];
}

/** Defender Vulnerability Management through the same registration (backend services/defenderTvm.ts). */
interface VulnerabilityManagement {
  enabled: boolean;
  lastSync: string | null;
  error: string | null;
  machines: number;
  vulnerabilities: number;
  requiredPermissions: string[];
}

interface SecureScore {
  current: number;
  max:     number;
  pct:     number;
  date:    string;
}

interface Summary {
  totalUsers:       number;
  mfaRegistered:    number;
  mfaNotRegistered: number;
  totalAlerts:      number;
  highAlerts:       number;
  totalPolicies:    number;
  enabledPolicies:  number;
  labelCount:       number;
  auditCount:       number;
  secureScore:      SecureScore | null;
}

interface M365SecurityStatus {
  connected: boolean;
  tenantId:  string | null;
  clientId:  string | null;
  lastSync:  string | null;
  settings:  Settings;
  vulnerabilityManagement?: VulnerabilityManagement;
  supplierGuests?: SupplierGuests;
  summary:   Summary;
}

interface UserRow {
  id:               string;
  upn:              string;
  displayName:      string;
  accountEnabled:   boolean;
  mfaRegistered:    boolean;
  methodsRegistered: string[];
  isAdmin:          boolean;
  lastSignInAt:     string | null;
}

interface AlertRow {
  id:          string;
  alertId:     string;
  title:       string;
  severity:    string;
  status:      string;
  category:    string | null;
  productName: string | null;
  occurredAt:  string;
}

interface CaPolicyRow {
  id:          string;
  policyId:    string;
  displayName: string;
  state:       string;
}

type DetailTab = "identity" | "alerts" | "ca" | "score" | "dlp" | "audit";

const SEVERITY_COLORS: Record<string, { bg: string; text: string }> = {
  high:          { bg: "var(--c-dangerBg)",  text: "var(--c-dangerText)" },
  medium:        { bg: "var(--c-warningBg)", text: "var(--c-warningText)" },
  low:           { bg: "var(--c-faint)",     text: "var(--c-muted)" },
  informational: { bg: "var(--c-faint)",     text: "var(--c-muted)" },
};

const STATE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  enabled:                          { bg: "var(--c-successBg)",  text: "var(--c-successText)", label: "Enabled" },
  disabled:                         { bg: "var(--c-faint)",      text: "var(--c-muted)",       label: "Disabled" },
  enabledForReportingButNotEnforced:{ bg: "var(--c-warningBg)", text: "var(--c-warningText)", label: "Report-only" },
};

function Pill({ label, bg, text }: { label: string; bg: string; text: string }) {
  return (
    <span style={{ display: "inline-block", padding: "2px 9px", borderRadius: 3, fontSize: 11, fontWeight: 600, background: bg, color: text, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function Toggle({ enabled, onChange, label }: { enabled: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", userSelect: "none" }}>
      <span style={{
        display: "inline-block", width: 36, height: 20, borderRadius: 3,
        background: enabled ? "var(--c-accent)" : "var(--c-border)",
        position: "relative", transition: "background 0.2s", flexShrink: 0,
      }}>
        <span style={{
          position: "absolute", top: 3, left: enabled ? 18 : 3,
          width: 14, height: 14, borderRadius: 2,
          background: "#fff", transition: "left 0.2s",
        }} />
      </span>
      <input type="checkbox" checked={enabled} onChange={(e) => onChange(e.target.checked)} style={{ display: "none" }} />
      <span style={{ color: theme.color.text }}>{label}</span>
    </label>
  );
}

export function ConnectM365SecurityPage() {
  const [status,      setStatus]      = useState<M365SecurityStatus | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [syncing,     setSyncing]     = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [collecting,  setCollecting]  = useState(false);
  const [collectResult, setCollectResult] = useState<{ controlsEvidenced: string[]; caPoliciesCount: number } | null>(null);
  const [collectError,  setCollectError]  = useState<string | null>(null);
  const [error,       setError]       = useState<string | null>(null);
  const [showForm,    setShowForm]    = useState(false);
  const [detailTab,   setDetailTab]   = useState<DetailTab>("identity");

  const [tenantId,     setTenantId]     = useState("");
  const [clientId,     setClientId]     = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const [users,    setUsers]    = useState<UserRow[]>([]);
  const [alerts,   setAlerts]   = useState<AlertRow[]>([]);
  const [policies, setPolicies] = useState<CaPolicyRow[]>([]);

  const [userFilter,  setUserFilter]  = useState("all");
  const [alertFilter, setAlertFilter] = useState("active");
  // Which alerts already have a register incident (Operational Resilience), and whether this member may raise one.
  const incidentLinks = useIncidentSourceLinks(!!status?.connected);

  const loadStatus = useCallback(async () => {
    try {
      const data = await api.get<M365SecurityStatus>("/m365-security/status");
      setStatus(data);
    } catch {
      setError("Failed to load M365 Security status.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!status?.connected) return;
    api.get<{ users: UserRow[] }>(`/m365-security/users?filter=${userFilter}`)
      .then((d) => setUsers(d.users))
      .catch(() => {});
  }, [status?.connected, userFilter]);

  useEffect(() => {
    if (!status?.connected) return;
    api.get<{ alerts: AlertRow[] }>(`/m365-security/alerts?filter=${alertFilter}`)
      .then((d) => setAlerts(d.alerts))
      .catch(() => {});
    api.get<{ policies: CaPolicyRow[] }>("/m365-security/ca-policies")
      .then((d) => setPolicies(d.policies))
      .catch(() => {});
  }, [status?.connected, alertFilter]);

  async function handleConnect(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.post("/m365-security/connect", { tenantId, clientId, clientSecret });
      setShowForm(false);
      setTenantId(""); setClientId(""); setClientSecret("");
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Connection failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleSync() {
    setSyncing(true);
    setError(null);
    try {
      await api.post("/m365-security/sync", {});
      await new Promise((r) => setTimeout(r, 4000));
      await loadStatus();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  async function handleAutoCollect() {
    setCollecting(true);
    setCollectResult(null);
    setCollectError(null);
    try {
      const result = await api.post<{
        collected: boolean;
        summary: { mfaEnabled: boolean; caPoliciesCount: number; controlsEvidenced: string[]; partialPermissions?: string[] };
      }>("/m365-security/auto-collect-evidence", {});
      setCollectResult({ controlsEvidenced: result.summary.controlsEvidenced, caPoliciesCount: result.summary.caPoliciesCount });
      if (result.summary.partialPermissions?.length) {
        setCollectError(`Partial collection — additional Graph permissions needed: ${result.summary.partialPermissions.join(", ")}`);
      }
    } catch (err: unknown) {
      if (err instanceof ApiError && err.code === "insufficient_graph_permissions") {
        const missing = (err.payload as { missing?: string[] })?.missing ?? [];
        setCollectError(`Additional Graph permissions required: ${missing.length ? missing.join(", ") : "Policy.Read.All, Reports.Read.All"}`);
      } else {
        setCollectError(err instanceof Error ? err.message : "Auto-collection failed");
      }
    } finally {
      setCollecting(false);
    }
  }

  async function handleDisconnect() {
    if (!confirm("Remove M365 Security connector and delete all synced data?")) return;
    setDisconnecting(true);
    try {
      await api.del("/m365-security/connect");
      setUsers([]); setAlerts([]); setPolicies([]);
      await loadStatus();
    } catch {
      setError("Disconnect failed");
    } finally {
      setDisconnecting(false);
    }
  }

  async function handleSettingToggle(key: keyof Settings, val: boolean) {
    if (!status) return;
    try {
      await api.patch("/m365-security/settings", { [key]: val });
      setStatus((prev) => prev ? { ...prev, settings: { ...prev.settings, [key]: val } } : prev);
    } catch {
      setError("Failed to save setting");
    }
  }

  if (loading) return <LoadingBlock label="Loading M365 Security status…" />;
  if (!status) return <div style={{ color: "var(--c-dangerText)", padding: "20px" }}>{error || "Failed to load status"}</div>;

  const s = status;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <LockKeyhole size={22} color="var(--c-accentText)" />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>Microsoft 365 Security</h2>
            {s.connected && (
              <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 3 }}>Connected</span>
            )}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle }}>
            Pull identity, MFA status, Secure Score, Defender alerts, Conditional Access policies, and Purview DLP labels from your Microsoft 365 E3/E5 tenant.
          </p>
        </div>
        {s.connected && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button onClick={handleAutoCollect} disabled={collecting} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13, opacity: collecting ? 0.6 : 1, cursor: collecting ? "not-allowed" : "pointer" }}>
              {collecting ? "Collecting…" : "Auto-collect Entra ID evidence"}
            </button>
            <button onClick={handleSync} disabled={syncing} style={{ padding: "7px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.text, cursor: syncing ? "not-allowed" : "pointer", opacity: syncing ? 0.6 : 1 }}>
              {syncing ? "Syncing…" : "Sync Now"}
            </button>
            <button onClick={handleDisconnect} disabled={disconnecting} style={{ padding: "7px 14px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: "var(--c-dangerText)", cursor: "pointer", opacity: disconnecting ? 0.6 : 1 }}>
              Disconnect
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>
          {error}
        </div>
      )}

      {collectResult && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-successBg)", border: "1px solid var(--c-successText)", color: "var(--c-successText)", fontSize: 13 }}>
          Collected {collectResult.controlsEvidenced.length} control{collectResult.controlsEvidenced.length !== 1 ? "s" : ""} evidenced — see your Documents library.
          {collectResult.controlsEvidenced.length > 0 && (
            <span style={{ marginLeft: 6, opacity: 0.8 }}>({collectResult.controlsEvidenced.join(", ")})</span>
          )}
        </div>
      )}

      {collectError && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-warningBg)", border: "1px solid var(--c-warningText)", color: "var(--c-warningText)", fontSize: 13 }}>
          {collectError}
        </div>
      )}

      {/* Not connected — placeholder */}
      {!s.connected && !showForm && (
        <div style={{ ...card, textAlign: "center", padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Connect Microsoft 365 Security</div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 560, margin: "0 auto 6px", lineHeight: 1.6 }}>
            Create an app registration in your Azure tenant and grant it the following <strong>Application permissions</strong>:
          </p>
          <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, textAlign: "left", margin: "8px auto 20px", fontSize: 12, fontFamily: "monospace", background: "var(--c-faint)", borderRadius: 3, padding: "12px 18px" }}>
            {[
              "User.Read.All",
              "UserAuthenticationMethod.Read.All",
              "AuditLog.Read.All",
              "Policy.Read.All",
              "SecurityEvents.Read.All",
              "InformationProtectionPolicy.Read.All  (for DLP labels)",
              "Directory.Read.All",
            ].map((p) => <span key={p}>{p}</span>)}
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 560, margin: "0 auto 6px", lineHeight: 1.6 }}>
            For Defender Vulnerability Management (which CVEs each device carries), also add these on the <strong>WindowsDefenderATP</strong> API:
          </p>
          <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, textAlign: "left", margin: "8px auto 20px", fontSize: 12, fontFamily: "monospace", background: "var(--c-faint)", borderRadius: 3, padding: "12px 18px" }}>
            {["Machine.Read.All", "Vulnerability.Read.All"].map((p) => <span key={p}>{p}</span>)}
          </div>
          <p style={{ fontSize: 13, color: theme.color.muted, maxWidth: 560, margin: "0 auto 6px", lineHeight: 1.6 }}>
            Optional, for the features that use them — add only what you will use:
          </p>
          <div style={{ display: "inline-flex", flexDirection: "column", gap: 4, textAlign: "left", margin: "8px auto 20px", fontSize: 12, fontFamily: "monospace", background: "var(--c-faint)", borderRadius: 3, padding: "12px 18px" }}>
            {[
              "ActivityFeed.ReadDlp  (Office 365 Management APIs: Data discovery from Purview DLP)",
              "Machine.Scan  (WindowsDefenderATP: remediation playbooks that start an antivirus scan)",
              "Machine.Isolate  (WindowsDefenderATP: remediation playbooks that isolate a device)",
            ].map((p) => <span key={p}>{p}</span>)}
          </div>
          <p style={{ fontSize: 12, color: theme.color.muted, maxWidth: 560, margin: "0 auto 20px" }}>
            A device action only ever runs after a named person approves it on the Remediation page. Without these
            permissions the rest of the connection works as normal; the feature that needs one says so.
          </p>
          <p style={{ fontSize: 12, color: theme.color.muted, maxWidth: 520, margin: "0 auto 20px" }}>
            Then grant admin consent in the portal and paste the credentials below. Data syncs every 6 hours.
          </p>
          <button onClick={() => setShowForm(true)} style={{ ...btnPrimary, padding: "9px 22px", fontSize: 13, cursor: "pointer" }}>
            Enter Credentials
          </button>
        </div>
      )}

      {/* Connect form */}
      {!s.connected && showForm && (
        <div style={{ ...card }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 600, color: theme.color.heading }}>App Registration Credentials</h3>
          <form onSubmit={handleConnect} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {[
              { label: "Directory (Tenant) ID", value: tenantId, onChange: setTenantId, type: "text", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
              { label: "Application (Client) ID", value: clientId, onChange: setClientId, type: "text", placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" },
              { label: "Client Secret (Value)", value: clientSecret, onChange: setClientSecret, type: "password", placeholder: "Secret value from Azure" },
            ].map(({ label, value, onChange, type, placeholder }) => (
              <label key={label} style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
                <span style={{ fontWeight: 500, color: theme.color.text }}>{label}</span>
                <input
                  type={type}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  placeholder={placeholder}
                  required
                  style={{ ...inputStyle, fontFamily: type === "text" ? "monospace" : undefined, fontSize: 13 }}
                />
              </label>
            ))}
            <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
              <button type="submit" disabled={saving} style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13, cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.6 : 1 }}>
                {saving ? "Validating & Connecting…" : "Connect"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} style={{ padding: "9px 16px", fontSize: 13, borderRadius: 3, border: "1px solid var(--c-border)", background: "var(--c-panel)", color: theme.color.muted, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Connected — summary + detail */}
      {s.connected && (
        <>
          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 12, marginBottom: 20 }}>
            {[
              { label: "Secure Score", value: s.summary.secureScore ? `${s.summary.secureScore.pct}%` : "—", sub: s.summary.secureScore ? `${s.summary.secureScore.current}/${s.summary.secureScore.max}` : "", bg: "var(--c-accentBg)", text: "var(--c-accentText)" },
              { label: "Without MFA", value: s.summary.mfaNotRegistered, sub: `of ${s.summary.totalUsers} users`, bg: s.summary.mfaNotRegistered > 0 ? "var(--c-dangerBg)" : "var(--c-successBg)", text: s.summary.mfaNotRegistered > 0 ? "var(--c-dangerText)" : "var(--c-successText)" },
              { label: "High Alerts", value: s.summary.highAlerts, sub: `${s.summary.totalAlerts} total`, bg: s.summary.highAlerts > 0 ? "var(--c-dangerBg)" : "var(--c-faint)", text: s.summary.highAlerts > 0 ? "var(--c-dangerText)" : "var(--c-muted)" },
              { label: "CA Policies", value: `${s.summary.enabledPolicies}/${s.summary.totalPolicies}`, sub: "enabled", bg: theme.color.panel, text: theme.color.heading },
              { label: "DLP Labels", value: s.summary.labelCount, sub: "sensitivity labels", bg: theme.color.panel, text: theme.color.heading },
            ].map((stat) => (
              <div key={stat.label} style={{ ...card, background: stat.bg, padding: "14px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 22, fontWeight: 700, color: stat.text }}>{stat.value}</div>
                <div style={{ fontSize: 11, color: stat.text, marginTop: 2, opacity: 0.85 }}>{stat.label}</div>
                {stat.sub && <div style={{ fontSize: 10, color: stat.text, opacity: 0.65, marginTop: 2 }}>{stat.sub}</div>}
              </div>
            ))}
          </div>

          {/* Continuous monitoring: the daily readings over 90 days */}
          <ConnectorTrendPanel connector="m365_security" />

          {/* Meta + settings */}
          <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
            {/* Meta info */}
            <div style={{ ...card, padding: "12px 16px", flex: "0 0 auto", fontSize: 12, color: theme.color.muted, display: "flex", flexDirection: "column", gap: 4 }}>
              <span>Tenant: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{s.tenantId}</strong></span>
              <span>Client: <strong style={{ color: theme.color.text, fontFamily: "monospace" }}>{s.clientId}</strong></span>
              <span>Last sync: <strong style={{ color: theme.color.text }}>{fmtDate(s.lastSync)}</strong></span>
            </div>
            {/* Feature toggles */}
            <div style={{ ...card, padding: "12px 18px", flex: "1 1 300px" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, marginBottom: 10 }}>SYNC CATEGORIES</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 24px" }}>
                <Toggle enabled={s.settings.identityEnabled} onChange={(v) => handleSettingToggle("identityEnabled", v)} label="Identity & MFA" />
                <Toggle enabled={s.settings.scoreEnabled}    onChange={(v) => handleSettingToggle("scoreEnabled", v)}    label="Secure Score" />
                <Toggle enabled={s.settings.alertsEnabled}   onChange={(v) => handleSettingToggle("alertsEnabled", v)}   label="Security Alerts" />
                <Toggle enabled={s.settings.caEnabled}       onChange={(v) => handleSettingToggle("caEnabled", v)}       label="CA Policies" />
                <Toggle enabled={s.settings.dlpEnabled}      onChange={(v) => handleSettingToggle("dlpEnabled", v)}      label="DLP Labels" />
                <Toggle enabled={s.settings.auditEnabled}    onChange={(v) => handleSettingToggle("auditEnabled", v)}    label="Audit Log" />
                <Toggle enabled={s.settings.vulnEnabled}     onChange={(v) => handleSettingToggle("vulnEnabled", v)}     label="Defender Vulnerability Management" />
                <Toggle enabled={!!s.settings.guestEnabled}  onChange={(v) => handleSettingToggle("guestEnabled", v)}    label="Supplier guest accounts" />
              </div>
            </div>
          </div>

          {/* Defender Vulnerability Management — the per-device CVE list the vulnerability exposure pass reads */}
          {s.vulnerabilityManagement && (
            <div style={{ ...card, padding: "12px 18px", marginBottom: 20, fontSize: 12, color: theme.color.muted, display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted }}>DEFENDER VULNERABILITY MANAGEMENT</div>
              {!s.vulnerabilityManagement.enabled ? (
                <span>Switched off. Turn it on to read each machine, its software inventory and the CVEs Defender reports, so Threat Intelligence can confirm which advisories affect your devices.</span>
              ) : s.vulnerabilityManagement.error ? (
                <span style={{ color: "var(--c-warningText)" }}>
                  {s.vulnerabilityManagement.error}. Add {s.vulnerabilityManagement.requiredPermissions.map((p, i) => <span key={p}>{i > 0 ? " and " : ""}<code>{p}</code></span>)} as application permissions on the <strong>WindowsDefenderATP</strong> API of this app registration, grant admin consent, then sync again.
                </span>
              ) : (
                <span>
                  {s.vulnerabilityManagement.machines.toLocaleString()} machine{s.vulnerabilityManagement.machines === 1 ? "" : "s"}, {s.vulnerabilityManagement.vulnerabilities.toLocaleString()} open Critical / High or feed-listed CVE{s.vulnerabilityManagement.vulnerabilities === 1 ? "" : "s"} on the register
                  {s.vulnerabilityManagement.lastSync ? ` · last read ${fmtDate(s.vulnerabilityManagement.lastSync)}` : " · not read yet — runs with the next sync"}. Machines Intune does not manage become assets of their own; every CVE feeds the exposure verdicts on Threat Intelligence.
                </span>
              )}
            </div>
          )}

          {/* Supplier guest accounts — the supplier register's "Guest access to your tenant" signal */}
          {s.supplierGuests && (
            <div style={{ ...card, padding: "12px 18px", marginBottom: 20, fontSize: 12, color: theme.color.muted, display: "grid", gap: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted }}>SUPPLIER GUEST ACCOUNTS</div>
              {!s.settings.guestEnabled ? (
                <span>Switched off. Turn it on to read the guest accounts suppliers' people hold in this tenant and their recent sign-ins, matched to suppliers by domain. Used only while the supplier register (TPRM add-on) is on.</span>
              ) : !s.supplierGuests.available ? (
                <span>Switched on, but the supplier register (TPRM add-on) is off for your organisation, so guest accounts are not read. It starts with the next sync once the add-on is on.</span>
              ) : s.supplierGuests.error ? (
                <span style={{ color: "var(--c-warningText)" }}>
                  {s.supplierGuests.error}. Add {s.supplierGuests.requiredPermissions.map((p, i) => <span key={p}>{i > 0 ? " and " : ""}<code>{p}</code></span>)} as application permissions on <strong>Microsoft Graph</strong> for this app registration, grant admin consent, then sync again.
                </span>
              ) : (
                <span>
                  {s.supplierGuests.accounts.toLocaleString()} guest account{s.supplierGuests.accounts === 1 ? "" : "s"}, {s.supplierGuests.matched.toLocaleString()} matched to a supplier
                  {s.supplierGuests.lastSync ? ` · last read ${fmtDate(s.supplierGuests.lastSync)}` : " · not read yet — runs with the next sync"}. Each supplier's accounts show on its Assurance tab on the Suppliers page.
                </span>
              )}
            </div>
          )}

          {/* Detail tabs */}
          <IndexTabs
            items={[
              { id: "identity" as DetailTab, label: "Identity & MFA" },
              { id: "alerts"   as DetailTab, label: "Security Alerts" },
              { id: "ca"       as DetailTab, label: "CA Policies" },
              { id: "score"    as DetailTab, label: "Secure Score" },
              { id: "dlp"      as DetailTab, label: "DLP Labels" },
              { id: "audit"    as DetailTab, label: "Audit Log" },
            ]}
            value={detailTab}
            onChange={setDetailTab}
            style={{ marginBottom: 16 }}
          />

          {/* ── Identity & MFA tab ── */}
          {detailTab === "identity" && (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
                {(["all", "no-mfa", "mfa", "admin", "disabled"] as const).map((f) => {
                  const active = userFilter === f;
                  const labels: Record<string, string> = { "all": "All", "no-mfa": "No MFA", "mfa": "MFA Enabled", "admin": "Admins", "disabled": "Disabled" };
                  return (
                    <button key={f} onClick={() => setUserFilter(f)} style={{ padding: "4px 12px", borderRadius: 3, fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)", background: active ? "var(--c-accentBg)" : "var(--c-panel)", color: active ? "var(--c-accentText)" : theme.color.muted }}>
                      {labels[f]}
                    </button>
                  );
                })}
              </div>
              {users.length === 0 ? (
                <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>No users match this filter.</div>
              ) : (
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt }}>
                        {["User", "MFA", "Methods", "Admin", "Last Sign-in"].map((h) => (
                          <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u, i) => (
                        <tr key={u.id} style={{ borderBottom: i < users.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                          <td style={{ padding: "10px 14px", maxWidth: 240 }}>
                            <div style={{ fontWeight: 500, color: u.accountEnabled ? theme.color.heading : theme.color.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.displayName}</div>
                            <div style={{ fontSize: 11, color: theme.color.subtle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.upn}</div>
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            <Pill label={u.mfaRegistered ? "Registered" : "Not registered"} bg={u.mfaRegistered ? "var(--c-successBg)" : "var(--c-dangerBg)"} text={u.mfaRegistered ? "var(--c-successText)" : "var(--c-dangerText)"} />
                          </td>
                          <td style={{ padding: "10px 14px", color: theme.color.muted, fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {(u.methodsRegistered as string[]).join(", ") || "—"}
                          </td>
                          <td style={{ padding: "10px 14px" }}>
                            {u.isAdmin && <Pill label="Admin" bg="var(--c-warningBg)" text="var(--c-warningText)" />}
                          </td>
                          <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap", fontSize: 12 }}>{fmtDate(u.lastSignInAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ── Security Alerts tab ── */}
          {detailTab === "alerts" && (
            <>
              <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                {(["active", "high", "resolved"] as const).map((f) => {
                  const active = alertFilter === f;
                  const labels = { active: "Active", high: "High Severity", resolved: "Resolved" };
                  return (
                    <button key={f} onClick={() => setAlertFilter(f)} style={{ padding: "4px 12px", borderRadius: 3, fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", border: active ? `2px solid ${theme.color.accent}` : "1px solid var(--c-border)", background: active ? "var(--c-accentBg)" : "var(--c-panel)", color: active ? "var(--c-accentText)" : theme.color.muted }}>
                      {labels[f]}
                    </button>
                  );
                })}
              </div>
              {alerts.length === 0 ? (
                <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>No alerts match this filter.</div>
              ) : (
                <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt }}>
                        {["Title", "Severity", "Status", "Category", "Occurred", "Incident"].map((h) => (
                          <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {alerts.map((a, i) => {
                        const sev = SEVERITY_COLORS[a.severity] ?? { bg: "var(--c-faint)", text: "var(--c-muted)" };
                        return (
                          <tr key={a.id} style={{ borderBottom: i < alerts.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                            <td style={{ padding: "10px 14px", maxWidth: 280 }}>
                              <div style={{ fontWeight: 500, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.title}</div>
                              {a.productName && <div style={{ fontSize: 11, color: theme.color.subtle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.productName}</div>}
                            </td>
                            <td style={{ padding: "10px 14px" }}><Pill label={a.severity} bg={sev.bg} text={sev.text} /></td>
                            <td style={{ padding: "10px 14px", color: theme.color.muted, textTransform: "capitalize" }}>{a.status}</td>
                            <td style={{ padding: "10px 14px", color: theme.color.muted, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.category ?? "—"}</td>
                            <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap", fontSize: 12 }}>{fmtDate(a.occurredAt)}</td>
                            <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}><RaiseIncidentCell kind="m365_alert" sourceId={a.id} links={incidentLinks.links} canRaise={incidentLinks.canRaise} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ── CA Policies tab ── */}
          {detailTab === "ca" && (
            policies.length === 0 ? (
              <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>No Conditional Access policies found.</div>
            ) : (
              <div style={{ ...card, padding: 0, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt }}>
                      {["Policy Name", "State"].map((h) => (
                        <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {policies.map((p, i) => {
                      const st = STATE_COLORS[p.state] ?? { bg: "var(--c-faint)", text: "var(--c-muted)", label: p.state };
                      return (
                        <tr key={p.id} style={{ borderBottom: i < policies.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
                          <td style={{ padding: "10px 14px", fontWeight: 500, color: theme.color.heading, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.displayName}</td>
                          <td style={{ padding: "10px 14px" }}><Pill label={st.label} bg={st.bg} text={st.text} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* ── Secure Score tab ── */}
          {detailTab === "score" && (
            <SecureScorePanel orgId={null} connected={s.connected} score={s.summary.secureScore} />
          )}

          {/* ── DLP Labels tab ── */}
          {detailTab === "dlp" && (
            <DlpLabelsPanel connected={s.connected} enabled={s.settings.dlpEnabled} />
          )}

          {/* ── Audit Log tab ── */}
          {detailTab === "audit" && (
            <AuditPanel connected={s.connected} enabled={s.settings.auditEnabled} count={s.summary.auditCount} />
          )}
        </>
      )}
    </div>
  );
}

// ── Sub-panel components ────────────────────────────────────────────────────

function SecureScorePanel({ score, connected }: { orgId: null; connected: boolean; score: SecureScore | null }) {
  const [detail, setDetail] = useState<{ controlScores: Array<{ controlName: string; score: number; maxScore: number; controlCategory: string }> } | null>(null);
  const [history, setHistory] = useState<Array<{ currentScore: number; maxScore: number; scoreDate: string }>>([]);

  useEffect(() => {
    if (!connected) return;
    api.get<{ score: { controlScores: Array<{ controlName: string; score: number; maxScore: number; controlCategory: string }> } | null }>("/m365-security/secure-score")
      .then((d) => setDetail(d.score))
      .catch(() => {});
    api.get<{ history: Array<{ currentScore: number; maxScore: number; scoreDate: string }> }>("/m365-security/secure-score-history")
      .then((d) => setHistory(d.history ?? []))
      .catch(() => {});
  }, [connected]);

  if (!score) return <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>No Secure Score data yet. Enable the sync toggle and run a sync.</div>;

  const controls = detail?.controlScores ?? [];
  const byCategory = controls.reduce<Record<string, typeof controls>>((acc, c) => {
    const cat = c.controlCategory || "Other";
    (acc[cat] = acc[cat] || []).push(c);
    return acc;
  }, {});

  return (
    <div>
      <div style={{ ...card, padding: "20px 24px", marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 36, fontWeight: 700, color: "var(--c-accent)" }}>{score.pct}%</div>
            <div style={{ fontSize: 12, color: theme.color.muted }}>Secure Score</div>
          </div>
          <div style={{ flex: 1 }}>
            <RulerProgress value={score.pct} fill={score.pct >= 70 ? "var(--c-successText)" : score.pct >= 40 ? "var(--c-warningText)" : "var(--c-dangerText)"} label="Secure Score" />
            <div style={{ marginTop: 6, fontSize: 12, color: theme.color.muted }}>{score.current} / {score.max} points · as of {fmtDate(score.date)}</div>
          </div>
          {history.length >= 2 && (() => {
            // history is newest-first; reverse so oldest is left
            const pts = [...history].reverse();
            const scores = pts.map((p) => (p.maxScore > 0 ? (p.currentScore / p.maxScore) * 100 : 0));
            const minV = Math.min(...scores);
            const maxV = Math.max(...scores);
            const range = maxV - minV || 1;
            const W = 200; const H = 40; const PAD = 3;
            const points = scores.map((s, i) => {
              const x = PAD + (i / (scores.length - 1)) * (W - PAD * 2);
              const y = H - PAD - ((s - minV) / range) * (H - PAD * 2);
              return `${x.toFixed(1)},${y.toFixed(1)}`;
            }).join(" ");
            return (
              <svg viewBox={`0 0 ${W} ${H}`} style={{ width: W, height: H, flexShrink: 0 }} aria-label="Score trend">
                <polyline points={points} fill="none" stroke="var(--c-accent)" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
              </svg>
            );
          })()}
        </div>
      </div>
      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} style={{ ...card, marginBottom: 12, padding: "14px 16px" }}>
          <div style={{ fontWeight: 600, fontSize: 13, color: theme.color.heading, marginBottom: 10 }}>{cat}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {items.sort((a, b) => (a.score / a.maxScore) - (b.score / b.maxScore)).map((c) => {
              const pct = c.maxScore > 0 ? Math.round((c.score / c.maxScore) * 100) : 0;
              return (
                <div key={c.controlName} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ flex: 1, fontSize: 12, color: theme.color.text }}>{c.controlName.replace(/([A-Z])/g, " $1").trim()}</div>
                  <RulerProgress value={pct} fill={pct >= 70 ? "var(--c-successText)" : pct >= 40 ? "var(--c-warningText)" : "var(--c-dangerText)"} label={c.controlName} style={{ width: 120 }} />
                  <div style={{ width: 36, textAlign: "right", fontSize: 11, color: theme.color.muted }}>{c.score}/{c.maxScore}</div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

interface LabelRow { id: string; name: string; description: string | null; color: string | null; sensitivity: number | null; isEnabled: boolean }

function DlpLabelsPanel({ connected, enabled }: { connected: boolean; enabled: boolean }) {
  const [labels, setLabels] = useState<LabelRow[]>([]);

  useEffect(() => {
    if (!connected) return;
    api.get<{ labels: LabelRow[] }>("/m365-security/sensitivity-labels").then((d) => setLabels(d.labels)).catch(() => {});
  }, [connected]);

  if (!enabled) return <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>Enable the DLP Labels toggle above to sync sensitivity labels from Microsoft Purview.</div>;
  if (labels.length === 0) return <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>No sensitivity labels found. Ensure InformationProtectionPolicy.Read.All is granted.</div>;

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt }}>
            {["Label", "Sensitivity", "Status", "Description"].map((h) => (
              <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {labels.map((l, i) => (
            <tr key={l.id} style={{ borderBottom: i < labels.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
              <td style={{ padding: "10px 14px", maxWidth: 200 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, overflow: "hidden" }}>
                  {l.color && <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0 }} />}
                  <span style={{ fontWeight: 500, color: theme.color.heading, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.name}</span>
                </div>
              </td>
              <td style={{ padding: "10px 14px", color: theme.color.muted, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.sensitivity ?? "—"}</td>
              <td style={{ padding: "10px 14px" }}><Pill label={l.isEnabled ? "Enabled" : "Disabled"} bg={l.isEnabled ? "var(--c-successBg)" : "var(--c-faint)"} text={l.isEnabled ? "var(--c-successText)" : "var(--c-muted)"} /></td>
              <td style={{ padding: "10px 14px", color: theme.color.muted, fontSize: 12, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.description || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface AuditEventRow { id: string; category: string; activityName: string; result: string | null; actorEmail: string | null; occurredAt: string }

function AuditPanel({ connected, enabled, count }: { connected: boolean; enabled: boolean; count: number }) {
  const [events, setEvents] = useState<AuditEventRow[]>([]);

  useEffect(() => {
    if (!connected || !enabled) return;
    api.get<{ events: AuditEventRow[] }>("/m365-security/audit-events").then((d) => setEvents(d.events)).catch(() => {});
  }, [connected, enabled]);

  if (!enabled) return <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>Enable the Audit Log toggle to sync Entra ID directory audit events (last 7 days, capped at 200 per sync).</div>;
  if (count === 0 && events.length === 0) return <div style={{ ...emptyFrame, padding: "28px 20px", color: theme.color.muted, fontSize: 13 }}>No audit events yet. Run a sync.</div>;

  return (
    <div style={{ ...card, padding: 0, overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${theme.color.line}`, background: theme.color.panelAlt }}>
            {["Activity", "Category", "Actor", "Result", "Occurred"].map((h) => (
              <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {events.map((e, i) => (
            <tr key={e.id} style={{ borderBottom: i < events.length - 1 ? `1px solid ${theme.color.border}` : "none" }}>
              <td style={{ padding: "10px 14px", fontWeight: 500, color: theme.color.heading }}>{e.activityName}</td>
              <td style={{ padding: "10px 14px", color: theme.color.muted }}>{e.category}</td>
              <td style={{ padding: "10px 14px", color: theme.color.muted, fontSize: 12 }}>{e.actorEmail ?? "—"}</td>
              <td style={{ padding: "10px 14px" }}>
                {e.result && <Pill label={e.result} bg={e.result === "success" ? "var(--c-successBg)" : "var(--c-dangerBg)"} text={e.result === "success" ? "var(--c-successText)" : "var(--c-dangerText)"} />}
              </td>
              <td style={{ padding: "10px 14px", color: theme.color.muted, whiteSpace: "nowrap", fontSize: 12 }}>{fmtDate(e.occurredAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
